import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IStoreState } from "../initialStoreState";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IFeeTransition, IStudent } from "./student.types";
import { downloadSingleFileAsync } from "../../components/FileUploadIcon/helpers";

//******************************STUDENT FETCHING START *********************************** */

export const FETCH_CUSTOMER_FULL_DETAILS_PROGRESS =
  "FETCH_CUSTOMER_FULL_DETAILS_PROGRESS";
export const ADD_CUSTOMER_FULL_DETAILS_SUCCESS =
  "ADD_CUSTOMER_FULL_DETAILS_SUCCESS";
export const FETCH_CUSTOMER_FULL_DETAILS_FAILED =
  "FETCH_CUSTOMER_FULL_DETAILS_FAILED";

export const fetchStudentListProgress = () =>
  action(FETCH_CUSTOMER_FULL_DETAILS_PROGRESS);
export const fetchStudentListSuccess = (
  data: IStudent[],
  totalRecords: number
) =>
  action(ADD_CUSTOMER_FULL_DETAILS_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchStudentListFailed = () =>
  action(FETCH_CUSTOMER_FULL_DETAILS_FAILED);

export const fetchStudentsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    company_outlet_uuid?: string,
    searchByName?: string,
    searchByNo?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchStudentListProgress());
      dispatch(saveLoaderProgress());

      let url = "";

      if (status !== "-1") {
        url += `&status=${status}`;
      }
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      if (searchValue.length > 0 || searchByName || searchByNo) {
        const advanceFilter = [...searchValue];

        if (searchByName) {
          advanceFilter.push({
            column: ["customer_name"],
            value: searchByName,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }
        if (searchByNo) {
          advanceFilter.push({
            column: ["contact_number"],
            value: searchByNo,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        url += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      const finalUrl =
        `/customer/get-customer?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}` +
        url;

      const response = await api.get(finalUrl);
      if (response.status === 200 && response.data) {
        const data: IStudent[] = response.data.data;
        const totalRecords = response.data.totalRecords;
        dispatch(fetchStudentListSuccess(data, totalRecords));
      }
    } catch (err: any) {
      dispatch(fetchStudentListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//******************************STUDENT FETCHING END *********************************** */

//***********STUDENT SINGLE DATA FETCH START ******************************************* */
export const FETCH_STUDENT_PROGRESS = "FETCH_STUDENT_PROGRESS";
export const FETCH_STUDENT_SUCCESS = "FETCH_STUDENT_SUCCESS";
export const FETCH_STUDENT_FAILED = "FETCH_STUDENT_FAILED";

export const fetchStudentProgress = () => action(FETCH_STUDENT_PROGRESS);
export const fetchStudentSuccess = (Student: IStudent) =>
  action(FETCH_STUDENT_SUCCESS, { Student });
export const fetchStudentFailed = (errorMessage: string) =>
  action(FETCH_STUDENT_FAILED, { errorMessage });

export const fetchStudentAsync =
  (
    student_uuid: string,
    company_outlet_uuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchStudentProgress());
      dispatch(saveLoaderProgress());
      let url = `/customer/get-customer?customer_fact_uuid=${student_uuid}`;
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }
      const res = await api.get(url);
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchStudentSuccess(data[0]));
      } else {
        dispatch(
          fetchStudentFailed(
            "Unfortunately, there are no records available at the moment."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchStudentFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//******************************STUDENT SINGLE DATA FETCH END *********************************** */

//******************************STUDENT UPSERT START *********************************** */
export const upsertStudentAysnc =
  (
    existingLead: IStudent,
    onCallBack: (isSuccess?: boolean, data?: IStudent) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(saveLoaderProgress());
    try {
      const {
        customer_personal_information_unique_id,
        customer_personal_information_id,
        created_by_uuid,
        created_by_name,
        create_ts,
        insert_ts,
        modified_by_name,
        modified_by_uuid,

        ...rest
      } = existingLead;

      const res = await api.post("/customer/upsert-customer", rest);
      dispatch(
        showMessage({
          type: "success",
          message: "Customer is saved succesfully!",
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallBack(true, data);
    } catch (err: any) {
      onCallBack(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

//********************************STUDENT UPSERT END *********************************** */

//********************************FEE TRANSITION START *********************************** */

export const FETCH_FEE_TRANINSTION_LIST_PROGRESS =
  "FETCH_FEE_TRANINSTION_LIST_PROGRESS";
export const FETCH_FEE_TRANINSTION_LIST_SUCCESS =
  "ADD_FEE_TRANINSTION_LIST_SUCCESS";
export const FETCH_FEE_TRANINSTION_LIST_FAILED =
  "FETCH_FEE_TRANINSTION_LIST_FAILED";

export const fetchFeeTransitionListProgress = () =>
  action(FETCH_FEE_TRANINSTION_LIST_PROGRESS);
export const fetchFeeTransitionListSuccess = (
  data: IFeeTransition[],
  totalRecords: number
) =>
  action(FETCH_FEE_TRANINSTION_LIST_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchFeeTransitionListFailed = () =>
  action(FETCH_FEE_TRANINSTION_LIST_FAILED);

export const fetchhFeeTransitionListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    studentFactId: string | null,
    date?: IDataTableV2DateState["dates"],
    status?: string,
    searchValue?: IDatatableV2AdvancedSearchFilter,
    company_outlet_uuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchFeeTransitionListProgress());

      let url = "";

      if (studentFactId !== null) {
        url += `&customer_fact_uuid=${studentFactId}`;
      }
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      const finalUrl =
        `/customer/get-fee-transaction?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}` +
        url;

      const response = await api.get(finalUrl);
      if (response.status === 200 && response.data) {
        const data: IFeeTransition[] = response.data.data;
        const totalRecords = response.data.totalRecords;
        dispatch(fetchFeeTransitionListSuccess(data, totalRecords));
      }
    } catch (err: any) {
      dispatch(fetchFeeTransitionListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message,
          displayAs: "snackbar",
        })
      );
    }
  };

//********************************FEE TRANSITION END *********************************** */

export const CLEAR_STUDENT_STATE = "CLEAR_STUDENT_STATE";
export const CLEAR_STUDENT = "CLEAR_STUDENT";

export const clearStudentsState = () => action(CLEAR_STUDENT_STATE);
export const clearStudent = () => action(CLEAR_STUDENT);

//******************************IMPORT CUSTOMERS FROM CSV START *********************************** */

export const importCustomersFromCSVAsync =
  (
    formData: FormData,
    company_outlet_uuid: string,
    onCallback: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const res = await api.post("/customer/import-customer", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: {
          company_outlet_uuid,
        },
      });

      dispatch(
        showMessage({
          type: "success",
          message: res.data.message || "Customers imported successfully!",
          displayAs: "snackbar",
        })
      );

      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to import customers",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
